import {
  ManagerEarningsResponse,
  ManagerStats,
  ManagerTenantDetail,
  ManagerTenantListResponse,
  ReferralLinkListResponse,
  ReferralLinkView,
  type CreateReferralLinkRequest,
  type ManagerContactEvent,
  type ManagerTenantListQuery,
  type UpdateReferralLinkRequest,
} from '@odonix/contracts';
import { periodQuery, type Period } from '../../ui/period.js';

/**
 * Клиники и лаборатории кабинета менеджера, `/v1/manager/tenants*`. Прямого
 * `fetch` в экранах нет — только этот клиент.
 */

type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>;

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function toError(response: Response): Promise<ApiError> {
  const body = (await response.json().catch(() => null)) as { code?: unknown; message?: unknown } | null;
  return typeof body?.code === 'string' && typeof body.message === 'string'
    ? new ApiError(body.code, body.message)
    : new ApiError('UNKNOWN', 'Сервер не ответил. Проверьте связь и повторите');
}

export interface TenantsApi {
  list(query: Partial<ManagerTenantListQuery>): Promise<ManagerTenantListResponse>;
  detail(tenantId: string): Promise<ManagerTenantDetail>;
  /** Отметка звонка или письма в журнал. Не ждёт ответа и не бросает:
   *  звонок важнее записи о нём, и телефон уже открывает звонилку. */
  contact(tenantId: string, event: ManagerContactEvent): void;
  stats(period: Period): Promise<ManagerStats>;
  earnings(period: Period): Promise<ManagerEarningsResponse>;
  links(): Promise<ReferralLinkView[]>;
  createLink(body: CreateReferralLinkRequest): Promise<ReferralLinkView>;
  updateLink(linkId: string, body: UpdateReferralLinkRequest): Promise<ReferralLinkView>;
}

export function createTenantsApi(apiFetch: ApiFetch): TenantsApi {
  async function get(path: string): Promise<unknown> {
    const response = await apiFetch(path);
    if (!response.ok) throw await toError(response);
    return response.json();
  }

  return {
    async list(query) {
      const params = new URLSearchParams();
      if (query.q !== undefined && query.q.trim() !== '') params.set('q', query.q.trim());
      if (query.kind !== undefined) params.set('kind', query.kind);
      if (query.state !== undefined) params.set('state', query.state);
      if (query.endingWithinDays !== undefined) params.set('endingWithinDays', String(query.endingWithinDays));
      if (query.sort !== undefined) params.set('sort', query.sort);
      if (query.cursor !== undefined) params.set('cursor', query.cursor);
      if (query.limit !== undefined) params.set('limit', String(query.limit));
      return ManagerTenantListResponse.parse(await get(`/v1/manager/tenants?${params.toString()}`));
    },

    async detail(tenantId) {
      return ManagerTenantDetail.parse(await get(`/v1/manager/tenants/${encodeURIComponent(tenantId)}`));
    },

    async stats(period) {
      return ManagerStats.parse(await get(`/v1/manager/stats?${periodQuery(period)}`));
    },

    async earnings(period) {
      return ManagerEarningsResponse.parse(await get(`/v1/manager/earnings?${periodQuery(period)}`));
    },

    async links() {
      return ReferralLinkListResponse.parse(await get('/v1/manager/links')).links;
    },

    async createLink(body) {
      const response = await apiFetch('/v1/manager/links', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw await toError(response);
      return ReferralLinkView.parse(await response.json());
    },

    async updateLink(linkId, body) {
      const response = await apiFetch(`/v1/manager/links/${encodeURIComponent(linkId)}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw await toError(response);
      return ReferralLinkView.parse(await response.json());
    },

    contact(tenantId, event) {
      // `keepalive` — запрос доживает, даже если страница в этот момент уходит
      // в звонилку или почтовую программу.
      void apiFetch(`/v1/manager/tenants/${encodeURIComponent(tenantId)}/contact`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(event),
        keepalive: true,
      }).catch(() => undefined);
    },
  };
}
